import { useNavigate, useLocation, useParams } from "react-router-dom";
import ProjectNav from "../components/ProjectNav";

export default function Review() {
  const { projectId } = useParams();
  const { state } = useLocation() as any;
  const navigate = useNavigate();
  const form = state?.form || {};

  const handleGenerate = () => {
    const evPath = state?.evMeta?.tmp_path || "";
    if (!evPath) {
      alert("EV file path is missing. Please go back and re-upload the EVM file.");
      return;
    }

    const processPayload = {
      project_id: projectId,
      ev_file_path: evPath,
      staffing_file_path: state?.staffingCentreMeta?.tmp_path || state?.staffingMeta?.tmp_path || "",
      staffing_weekly_file_path: state?.staffingWeeklyMeta?.tmp_path || "",
      jira_ro_file_path: state?.jiraRoMeta?.tmp_path || state?.jiraMeta?.tmp_path || "",
      jira_wp_file_path: state?.jiraWpMeta?.tmp_path || "",
      ims_file_path: state?.imsTmpPath || "",
      customer_feedback_file_path: state?.customerFeedbackMeta?.tmp_path || "",
      hitl_answers: {
        quarter: form.quarter || "2026-Q2",
        data_as_of: form.data_as_of || state?.dataAsOf || "",
        report_title: form.report_title || "",
        variance_threshold: parseFloat(form.variance_threshold) || 500000,
        cpi_threshold_red: parseFloat(form.cpi_red) || 0.90,
        cpi_threshold_amber: parseFloat(form.cpi_amber) || 0.95,
      },
    };

    // Navigate immediately — Processing page runs the API call so the user
    // doesn't wait on the Review page while files are being parsed.
    navigate(`/processing/${projectId}`, {
      state: { processPayload, reportTitle: form.report_title || "EAC Report", form },
    });
  };

  const InfoRow = ({ label, value }: any) => (
    <div>
      <p className="text-xs text-blue-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value || "—"}</p>
    </div>
  );

  const steps = ["Upload files", "Run Sanity Checks", "Configure", "Review & generate"];

  // Build file cards from state
  const files = [
    { label: "M170 EVM Data File", meta: state?.evMeta, required: true },
    { label: "Staffing Centre File", meta: state?.staffingCentreMeta || state?.staffingMeta, required: true },
    { label: "Staffing Weekly File", meta: state?.staffingWeeklyMeta, required: false },
    { label: "Jira R&O File", meta: state?.jiraRoMeta || state?.jiraMeta, required: false },
    { label: "Jira Work Package File", meta: state?.jiraWpMeta, required: false },
    { label: "Customer Feedback", meta: state?.customerFeedbackMeta, required: false },
  ];

  const evWarnings = state?.evMeta?.warnings || state?.sanityResult?.evm?.warnings || [];
  const staffingWarnings = state?.staffingCentreMeta?.warnings || state?.staffingMeta?.warnings || state?.sanityResult?.staffing?.warnings || [];
  const staffingWeeklyWarnings = state?.staffingWeeklyMeta?.warnings || state?.sanityResult?.staffing_weekly?.warnings || [];
  const jiraRoWarnings = state?.jiraMeta?.warnings || state?.jiraRoMeta?.warnings || state?.sanityResult?.jira_ro?.warnings || [];
  const jiraWpWarnings = state?.jiraWpMeta?.warnings || state?.sanityResult?.jira_wp?.warnings || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={projectId!} active="report" />

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">REVIEW & GENERATE</h1>
            <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide">
              Confirm everything looks right before generating the report.
            </p>
          </div>
          <div className="relative">
            <input placeholder="Search programs by name or code..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none w-64" />
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-0 mb-8 mt-6">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                  ${i < 3 ? "bg-green-500 text-white" : "bg-blue-600 text-white"}`}>
                  {i < 3 ? "✓" : i + 1}
                </div>
                <span className={`text-sm ${i < 3 ? "text-green-600" : "text-blue-600 font-medium"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && <div className="w-8 h-px bg-gray-300 mx-3"></div>}
            </div>
          ))}
        </div>

        {/* File summary cards — all 6 equal size */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {[
            {
              label: "M170 EVM Data File",
              meta: state?.evMeta,
              warnings: evWarnings,
              details: [
                ["Rows", state?.evMeta?.rows || "—"],
                ["Columns", state?.evMeta?.columns || "—"],
                ["Detected quarter", state?.evMeta?.detected_quarter || "—"],
                ["Size", state?.evMeta?.file_size_mb ? `${state.evMeta.file_size_mb} MB` : "—"],
              ]
            },
            {
              label: "Staffing Centre File",
              meta: state?.staffingCentreMeta || state?.staffingMeta,
              warnings: staffingWarnings,
              details: [
                ["Sheet", "SvsD_Pivot"],
                ["Size", (state?.staffingCentreMeta?.file_size_mb || state?.staffingMeta?.file_size_mb) ? `${state?.staffingCentreMeta?.file_size_mb || state?.staffingMeta?.file_size_mb} MB` : "—"],
              ]
            },
            {
              label: "Staffing Weekly",
              meta: state?.staffingWeeklyMeta,
              warnings: staffingWeeklyWarnings,
              details: [
                ["Sheet", "BP884"],
                ["Size", state?.staffingWeeklyMeta?.file_size_mb ? `${state.staffingWeeklyMeta.file_size_mb} MB` : "—"],
              ]
            },
            {
              label: "Jira R&O",
              meta: state?.jiraRoMeta || state?.jiraMeta,
              warnings: jiraRoWarnings,
              details: [
                ["Rows", state?.jiraRoMeta?.rows || state?.jiraMeta?.rows || "—"],
                ["Columns", state?.jiraRoMeta?.columns || state?.jiraMeta?.columns || "—"],
                ["Size", (state?.jiraRoMeta?.file_size_kb || state?.jiraMeta?.file_size_kb) ? `${state?.jiraRoMeta?.file_size_kb || state?.jiraMeta?.file_size_kb} KB` : "—"],
              ]
            },
            {
              label: "Jira Work Packages",
              meta: state?.jiraWpMeta,
              warnings: jiraWpWarnings,
              details: [
                ["Rows", state?.jiraWpMeta?.rows || "—"],
                ["Columns", state?.jiraWpMeta?.columns || "—"],
              ]
            },
          ].map(({ label, meta, warnings, details }) => (
            <div key={label} className={`bg-white rounded-xl border p-4 ${meta?.tmp_path ? "border-gray-200" : "border-gray-100"}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs text-gray-400">{label}</p>
                  <p className="text-sm font-semibold text-gray-900 truncate max-w-[180px]">
                    {meta?.filename || (meta?.tmp_path ? "Uploaded" : "Not uploaded")}
                  </p>
                </div>
                {meta?.tmp_path ? (
                  warnings.length > 0 ? (
                    <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded font-medium flex-shrink-0">
                      {warnings.length} WARNINGS
                    </span>
                  ) : (
                    <span className="text-xs bg-green-50 text-green-600 border border-green-200 px-2 py-0.5 rounded font-medium flex-shrink-0">
                      PASS
                    </span>
                  )
                ) : null}
              </div>
              {meta?.tmp_path ? (
                <>
                  <div className="border-t border-gray-100 pt-2 mb-2 space-y-1">
                    {details.map(([k, v]: any) => (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-gray-400">{k}</span>
                        <span className="font-medium text-gray-900">{v}</span>
                      </div>
                    ))}
                  </div>
                  {warnings.length > 0 && (
                    <div className="bg-amber-50 border border-amber-100 rounded-lg px-2 py-2">
                      {warnings.slice(0,2).map((w: string, i: number) => (
                        <p key={i} className="text-xs text-amber-600">• {w}</p>
                      ))}
                      {warnings.length > 2 && <p className="text-xs text-amber-500">+{warnings.length - 2} more</p>}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-400 mt-1">Not uploaded — optional</p>
              )}
            </div>
          ))}

          {/* IMS File — dynamic */}
          {(() => {
            const imsResult = state?.sanityResult?.results?.find((r: any) => r.file_type === "IMS");
            const imsFilename = state?.imsFilename;
            if (imsFilename && imsResult) {
              const imsStatus = imsResult.overall_status;
              const borderCls = imsStatus === "error" ? "border-red-200" : imsStatus === "warn" ? "border-amber-200" : "border-green-200";
              const badgeCls  = imsStatus === "error" ? "bg-red-50 text-red-600 border-red-200" : imsStatus === "warn" ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-green-50 text-green-600 border-green-200";
              const badgeText = imsStatus === "error" ? "ERROR" : imsStatus === "warn" ? "WARNINGS" : "READY";
              const titleCls  = imsStatus === "error" ? "text-red-700" : imsStatus === "warn" ? "text-amber-700" : "text-green-700";
              return (
                <div className={`bg-white rounded-xl border p-4 ${borderCls}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-xs text-gray-400">IMS File</p>
                      <p className={`text-sm font-semibold truncate max-w-[160px] ${titleCls}`}>{imsFilename}</p>
                    </div>
                    <span className={`text-xs border px-2 py-0.5 rounded font-medium flex-shrink-0 ${badgeCls}`}>{badgeText}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {imsResult.row_count} tasks · {imsResult.file_size_mb?.toFixed(1)} MB
                    {imsResult.error_count > 0 && <span className="text-red-500"> · {imsResult.error_count} error(s)</span>}
                    {imsResult.warning_count > 0 && <span className="text-amber-500"> · {imsResult.warning_count} warning(s)</span>}
                  </p>
                </div>
              );
            }
            return (
              <div className="bg-white rounded-xl border border-amber-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-xs text-gray-400">IMS File</p>
                    <p className="text-sm font-semibold text-amber-700">Not uploaded</p>
                  </div>
                  <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded font-medium flex-shrink-0">OPTIONAL</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">Upload an .mpp or .csv IMS file to show milestone data</p>
              </div>
            );
          })()}
        </div>

        {/* Configuration Recap */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Configuration Recap</p>
          <div className="grid grid-cols-5 gap-4 mb-4">
            <InfoRow label="Quarter" value={form.quarter || state?.month || "—"} />
            <InfoRow label="Data as of" value={state?.dataAsOf || form.data_as_of || "—"} />
            <InfoRow label="Report title" value={state?.reportTitle || form.report_title || "—"} />
            <InfoRow label="Variance threshold" value={`$${(parseFloat(form.variance_threshold) || 500000).toLocaleString()}`} />
            <InfoRow label="EVM ↔ Staffing join" value={state?.evmStaffingJoin || "WBS/CA/WP ↔ WBS_Code"} />
          </div>
          <div className="grid grid-cols-5 gap-4">
            <InfoRow label="Critical band (CPI/SPI)" value={`< ${form.cpi_red || "0.90"}`} />
            <InfoRow label="Warning band (CPI/SPI)" value={`< ${form.cpi_amber || "0.95"}`} />
            <InfoRow label="EAC method" value="Recalculate: ACWP + ETC" />
            <InfoRow label="Comparison baseline" value="Prior quarter" />
            <InfoRow label="EVM ↔ Jira join" value={state?.evmJiraJoin || "NWA ↔ Epic Link"} />
          </div>
        </div>

        {/* Bottom buttons */}
        <div className="flex justify-between">
          <div className="flex gap-2">
            <button onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              ← Back
            </button>
            <button onClick={() => navigate(`/configure/${projectId}`, { state })}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              ✎ Edit Answers
            </button>
          </div>
          <button
            onClick={handleGenerate}
            disabled={!state?.evMeta?.tmp_path}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
            Generate report →
          </button>
        </div>
      </div>
    </div>
  );
}
